import React, { useCallback, useEffect, useState } from "react";
import { Box, createTheme, ThemeProvider } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS } from "date-fns/locale";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import NumericInputField from "../common/NumericInputField";
import CustomAutoComplete from "../common/CustomAutoComplete";
import CustomTooltip from "../common/CustomTooltip";
import InfoIcon from "../../svg/InfoIcon";
import { enTimeUnit, formatTimeUnitWithParen } from "../seviceBuilder/helper";

// Time unit keys allowed for start offset (days through years)
const START_OFFSET_TIME_UNIT_KEYS = [4, 5, 6, 7] as const;

interface StartServiceElementProps {
  service: any;
  onServiceChange: (updatedService: any) => void;
  type?: string;
}

const StartServiceElement = ({
  service,
  onServiceChange,
  type = "logicBuilder",
}: StartServiceElementProps) => {
  const theme: Theme = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Initialize startDate from service when component mounts or service changes
  useEffect(() => {
    if (service.startDate) {
      setStartDate(new Date(service.startDate));
    }
  }, [service.startDate]);

  // Initialize endDate from service when component mounts or service changes
  useEffect(() => {
    if (service.endDate) {
      setEndDate(new Date(service.endDate));
    }
  }, [service.endDate]);

  const handleStartDateChange = useCallback(
    (newDate: Date | null) => {
      setStartDate(newDate);

      // Convert date to YYYY-MM-DD format to avoid timezone issues
      let dateString = null;
      if (newDate) {
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const day = String(newDate.getDate()).padStart(2, "0");
        dateString = `${year}-${month}-${day}`;
      }

      const updatedService = {
        ...service,
        startDate: dateString,
      };
      onServiceChange(updatedService);
    },
    [service, onServiceChange]
  );

  const handleEndDateChange = useCallback(
    (newDate: Date | null) => {
      setEndDate(newDate);

      // Convert date to YYYY-MM-DD format to avoid timezone issues
      let dateString = null;
      if (newDate) {
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const day = String(newDate.getDate()).padStart(2, "0");
        dateString = `${year}-${month}-${day}`;
      }

      const updatedService = {
        ...service,
        endDate: dateString,
      };
      onServiceChange(updatedService);
    },
    [service, onServiceChange]
  );

  // Create theme to match TextInputField styling
  const customTheme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              height: "56px",
              borderRadius: "8px",
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
              fontFamily: "var(--font-Medium)",
              fontSize: "12px",
              lineHeight: "20px",
              padding: "0 12px",
              "& input": {
                padding: "16px 0",
                transition: "padding 0.2s ease-in-out",
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                lineHeight: "20px",
              },
              "& .MuiInputAdornment-root": {
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                margin: 0,
              },
              "& fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
                top: 0,
              },
              "&:hover fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
                borderWidth: "1px",
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "var(--font-Medium)",
              fontSize: "10px",
              backgroundColor: "transparent",
              transition: "transform 0.2s ease-in-out",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              "&.Mui-focused": {
                color: theme.colors_ds.PRIMARY_ICON_MAIN,
                transform: "translate(12px, 6px) scale(1)",
              },
            },
            "& legend": {
              display: "none",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
            marginTop: "8px",
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          },
        },
      },
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          },
          label: {
            fontFamily: "var(--font-Bold)",
            fontSize: "14px",
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-selected='true']": {
              backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
              "&:hover": {
                backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              },
            },
            "&:hover": {
              backgroundColor: theme.colorTokens.N[40],
            },
          },
          today: {
            borderColor: "transparent",
            '&:not([aria-selected="true"])': {
              borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
            },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontFamily: "var(--font-Bold)",
            fontSize: "12px",
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          },
        },
      },
    },
  });

  if (type === "logicBuilder") {
    return (
      <Box sx={styles.startOffsetContainer}>
        <Box sx={styles.headerContainer}>
          <Box style={styles.title(theme)}>Start offset</Box>
          <CustomTooltip
            message={
              "The time passing until the service initiation after the report has been delivered."
            }
          >
            <InfoIcon color={theme.colors_ds.NEUTRAL_ICON_WEAK} size={16} />
          </CustomTooltip>
        </Box>
        <Box sx={styles.startOffsetInner}>
          <Box sx={styles.inputWrapper}>
            <NumericInputField
              label="Value"
              value={service.startOffset?.toString() || ""}
              handleChange={(e: any) => {
                const updatedService = {
                  ...service,
                  startOffset: parseFloat(e.target.value) || 0,
                };
                onServiceChange(updatedService);
              }}
              allowNegative={false}
            />
          </Box>
          <Box sx={styles.inputWrapper}>
            <CustomAutoComplete
              label="Unit"
              options={START_OFFSET_TIME_UNIT_KEYS.map((key) =>
                formatTimeUnitWithParen(enTimeUnit[key])
              )}
              value={
                service.startOffsetUnit && enTimeUnit[service.startOffsetUnit]
                  ? formatTimeUnitWithParen(enTimeUnit[service.startOffsetUnit])
                  : ""
              }
              onChange={(value: any) => {
                // Find the numeric key for the selected display value
                const unitKey = START_OFFSET_TIME_UNIT_KEYS.find(
                  (key) => formatTimeUnitWithParen(enTimeUnit[key]) === value
                );
                const updatedService = {
                  ...service,
                  startOffsetUnit: unitKey ?? null,
                };
                onServiceChange(updatedService);
              }}
              placeholder="Select unit"
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.headerContainer}>
        <Box style={styles.title(theme)}>Start date</Box>
        <CustomTooltip
          message={"The start date from which the service will be prescribed."}
        >
          <InfoIcon color={theme.colors_ds.NEUTRAL_ICON_WEAK} size={16} />
        </CustomTooltip>
      </Box>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
      >
        <ThemeProvider theme={customTheme}>
          <DatePicker
            label=""
            value={startDate}
            onChange={handleStartDateChange}
            format="MMM dd yyyy"
            minDate={(() => {
              if (service.startDate) {
                const existingDate = new Date(service.startDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                existingDate.setHours(0, 0, 0, 0);
                // If existing date is in the past, use it as minDate
                return existingDate < today ? existingDate : today;
              }
              return new Date();
            })()}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: "MMM DD YYYY",
                inputProps: {
                  readOnly: true,
                },
              },
            }}
          />
        </ThemeProvider>
      </LocalizationProvider>
    </Box>
  );
};

export default StartServiceElement;

const styles = {
  startOffsetContainer: {},
  startOffsetInner: {
    display: "flex",
    gap: "8px",
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    marginBottom: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
};
